/**
 * Turns a reanalysis mutation outcome into something the user can see.
 *
 * `analyzeRecording` has five failure exits and returns them as
 * `{success:false, error}` in the payload — not as a GraphQL error. The
 * recording list called the mutation and looked at neither, so pressing
 * Reanalyze produced no visible consequence whether the request was accepted,
 * rejected, or lost. Combined with the list showing `metadata.updatedAt`
 * (a human-edit timestamp) rather than `analysedAt`, a successful reanalysis
 * and a failed one look identical — which is what the July 2026 reports were.
 *
 * The rule that matters here: **silence is never success.** A missing or
 * unrecognisable payload is reported as an error, because claiming a
 * reanalysis was queued when we do not know that is exactly the failure this
 * whole change exists to remove.
 *
 * project_management#508
 */

export type FeedbackSeverity = 'success' | 'error';

export interface ReanalyzeFeedback {
  severity: FeedbackSeverity;
  message: string;
}

/** Shape of the mutation payload we care about (kept structural on purpose). */
export interface AnalyzeRecordingPayload {
  success?: boolean | null;
  error?: string | null;
}

export interface ReanalyzeOutcomeInput {
  /** Apollo `data` from the mutation result. */
  data?: { analyzeRecording?: AnalyzeRecordingPayload | null } | null;
  /** Apollo error (network failure or GraphQL errors), if the call rejected. */
  error?: { message?: string | null } | null;
  /** Recording file name, so a message is actionable when several rows exist. */
  fileName?: string | null;
}

/** `1641417.338` → ` for 1641417.338`; empty when unknown. */
function forRecording(fileName?: string | null): string {
  return fileName ? ` for ${fileName}` : '';
}

export function interpretReanalyzeOutcome(input: ReanalyzeOutcomeInput): ReanalyzeFeedback {
  const { data, error, fileName } = input;

  if (error) {
    const detail = error.message ? `: ${error.message}` : '';

    return {
      severity: 'error',
      message: `Reanalysis request${forRecording(fileName)} could not be sent${detail}`,
    };
  }

  const payload = data?.analyzeRecording;

  // No payload at all — do not guess. Reporting success here is the bug.
  if (!payload || payload.success === undefined || payload.success === null) {
    return {
      severity: 'error',
      message: `Reanalysis request${forRecording(fileName)} returned no result — it may not have been queued`,
    };
  }

  if (!payload.success) {
    const detail = payload.error ? `: ${payload.error}` : '';

    return {
      severity: 'error',
      message: `Reanalysis was rejected${forRecording(fileName)}${detail}`,
    };
  }

  return {
    severity: 'success',
    message: `Reanalysis queued${forRecording(fileName)}. The Analysis Status and rPeaks columns update when it finishes.`,
  };
}
